import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "../../components/ui/button";
import { Plus, ChevronDown, Archive, Shield, Eye, SquarePen, Check, UserPlus, Globe, UsersRound, Lock, Building2 } from "lucide-react";
import { cn } from "../../components/ui/utils";
import { ServiceDeskConfig, ServiceDeskDetails } from "../../components/service-desk/ServiceDeskDetails";
import MemberSelectModal from "../../components/service-desk/MemberSelectModal";
import AddGroupModal from "../../components/service-desk/AddGroupModal";
import RetireServiceDeskModal from "../../components/service-desk/RetireServiceDeskModal";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from "../../components/ui/dialog";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import api from "../../utils/api";
import { getRandomAvatarColor, getUserInitials } from "../../utils/avatar-utils";

export default function ServiceDeskSettings() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [desks, setDesks] = useState<ServiceDeskConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRetiredExpanded, setIsRetiredExpanded] = useState(false);

  // Add member modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isRetireModalOpen, setIsRetireModalOpen] = useState(false);
  const [isChangeAdminOpen, setIsChangeAdminOpen] = useState(false);
  const [isAddAuthorizedRequesterOpen, setIsAddAuthorizedRequesterOpen] = useState(false);
  
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedRole, setSelectedRole] = useState<'Team Admin' | 'Editor' | 'Viewer'>('Viewer');

  useEffect(() => {
    fetchDesks();
  }, []);

  // Automatic selection logic
  useEffect(() => {
    if (!isLoading && desks.length > 0 && !id) {
      const firstActive = desks.find(d => !d.isRetired);
      const targetId = firstActive ? firstActive.id : desks[0].id;
      navigate(`/service-desk/settings/${targetId}`, { replace: true });
    }
  }, [id, desks, isLoading, navigate]);

  const fetchDesks = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/api/service-desk');
      if (response.success) {
        const mappedDesks: ServiceDeskConfig[] = response.desks.map((d: any) => ({
          id: d.id.toString(),
          name: d.name,
          description: d.description,
          themeColor: d.themeColor,
          submissionControl: d.submissionControl,
          teamAdmin: {
            id: d.teamAdmin?.id,
            name: d.teamAdmin?.name || 'Unassigned',
            email: d.teamAdmin?.email || '',
            initials: getUserInitials(d.teamAdmin?.name || ''),
            color: getRandomAvatarColor(d.teamAdmin?.name || ''),
            archived: d.teamAdmin?.archived
          },
          members: d.members?.filter((m: any) => m.user && String(m.user.id) !== String(d.teamAdmin?.id)).map((m: any) => ({
            id: m.user.id,
            name: m.user.name,
            email: m.user.email,
            role: m.role.charAt(0) + m.role.slice(1).toLowerCase().replace('_', ' '),
            initials: getUserInitials(m.user.name),
            color: getRandomAvatarColor(m.user.name),
            archived: m.user.archived
          })) || [],
          authorizedRequesters: d.authorizedRequesters?.map((u: any) => ({
            id: u.id,
            name: u.name,
            email: u.email,
            initials: getUserInitials(u.name),
            color: getRandomAvatarColor(u.name),
            archived: u.archived
          })) || [],
          isRestricted: d.submissionControl === 'restricted',
          isRetired: d.isRetired,
          forms: d.forms?.filter((f: any) => !f.isRetired).map((f: any) => ({
            id: f.id.toString(),
            name: f.name,
            version: f.versions?.[0]?.versionNumber || 1,
            status: f.versions?.[0]?.status || 'Draft'
          })) || []
        }));
        setDesks(mappedDesks);
      }
    } catch (error) {
      console.error("Error fetching desks:", error);
      toast.error("Failed to load service desks.");
    } finally {
      setIsLoading(false);
    }
  };

  const activeDesks = desks.filter(d => !d.isRetired);
  const retiredDesks = desks.filter(d => d.isRetired);
  const selectedDesk = desks.find(d => d.id == id) || null;
  const isRetired = selectedDesk?.isRetired || false;

  const updateDeskOnServer = async (deskId: string, updates: any) => {
    try {
      const response = await api.put(`/api/service-desk/${deskId}`, updates);
      if (response.success) {
        await fetchDesks();
        // Notify sidebar and other components to refresh desk lists
        window.dispatchEvent(new CustomEvent('service-desks-changed'));
        return true;
      }
      return false;
    } catch (err) {
      toast.error("Failed to update service desk settings.");
      return false;
    }
  };

  const handleUserSelect = (user: any) => {
    if (selectedDesk?.members.some(m => m.email === user.email)) {
      toast.error(`${user.name} is already a member of ${selectedDesk.name}.`);
      return;
    }
    setSelectedUser(user);
    setIsAddModalOpen(false);
    setIsRoleModalOpen(true);
  };

  const handleConfirmAdd = async () => {
    if (!selectedUser || !selectedDesk) return;

    const hasAgentPerm = selectedUser?.permissions?.includes('manage:service_desk_agent');
    const hasViewerOnly = !hasAgentPerm && selectedUser?.permissions?.includes('manage:service_desk_agent_viewer');
    const hasTeamAdminPerm = selectedUser?.permissions?.includes('manage:service_desk_team_admin');

    if (hasViewerOnly && selectedRole !== 'Viewer') {
      toast.error(`${selectedUser.name} is restricted to the Viewer role based on system permissions.`);
      return;
    }

    if (selectedRole === 'Team Admin' && !hasTeamAdminPerm) {
      toast.error(`${selectedUser.name} does not have the required 'manage:service_desk_team_admin' permission to be a Team Administrator.`);
      return;
    }
    
    const role = selectedRole.toUpperCase().replace(' ', '_');
    let teamAdminId = selectedDesk.teamAdmin?.id;
    let newMembersList = [
        ...selectedDesk.members.map(m => ({ userId: m.id, role: (m.role || 'VIEWER').toUpperCase().replace(' ', '_') }))
    ];

    if (role === 'TEAM_ADMIN') {
        // Demote current admin to Editor
        if (teamAdminId) {
            newMembersList.push({ userId: teamAdminId, role: 'EDITOR' });
        }
        // Set new primary admin
        teamAdminId = selectedUser.id;
    } else {
        newMembersList.push({ userId: selectedUser.id, role });
    }

    const success = await updateDeskOnServer(selectedDesk.id, { 
        teamAdminId,
        members: newMembersList 
    });

    if (success) {
        setIsRoleModalOpen(false);
        setSelectedUser(null);
        setSelectedRole('Viewer');
        toast.success(`${selectedUser.name} has been added.`);
    }
  };

  const handleRemoveMember = async (email: string) => {
    if (!selectedDesk) return;
    const newMembersList = selectedDesk.members
        .filter(m => m.email !== email)
        .map(m => ({ userId: m.id, role: (m.role || 'VIEWER').toUpperCase().replace(' ', '_') }));
    
    const success = await updateDeskOnServer(selectedDesk.id, { members: newMembersList });
    if (success) {
        toast.success("Member removed.");
    }
  };

  const handleChangeAdmin = async (user: any) => {
    if (!selectedDesk) return;

    const hasAgentPerm = user?.permissions?.includes('manage:service_desk_agent');
    const hasViewerOnly = !hasAgentPerm && user?.permissions?.includes('manage:service_desk_agent_viewer');
    const hasTeamAdminPerm = user?.permissions?.includes('manage:service_desk_team_admin');

    if (hasViewerOnly) {
      toast.error(`${user.name} is restricted to the Viewer role and cannot be a Team Administrator.`);
      return;
    }

    if (!hasTeamAdminPerm) {
      toast.error(`${user.name} does not have the required 'manage:service_desk_team_admin' permission to be a Team Administrator.`);
      return;
    }

    // Demote old admin to Editor in the members list
    const oldAdmin = selectedDesk.teamAdmin;
    const newMembersList = [
        ...selectedDesk.members.map(m => ({ userId: m.id, role: (m.role || 'VIEWER').toUpperCase().replace(' ', '_') })),
    ];
    
    if (oldAdmin && oldAdmin.id) {
        if (!newMembersList.some(m => String(m.userId) === String(oldAdmin.id))) {
            newMembersList.push({ userId: oldAdmin.id, role: 'EDITOR' });
        }
    }

    const success = await updateDeskOnServer(selectedDesk.id, { 
        teamAdminId: user.id,
        members: newMembersList
    });

    if (success) {
        setIsChangeAdminOpen(false);
        toast.success(`${user.name} is now the Team Administrator.`);
    }
  };

  const handleAddAuthorizedRequester = async (user: any) => {
    if (!selectedDesk) return;
    if (selectedDesk.authorizedRequesters?.some(u => u.id === user.id)) {
        toast.error("User is already authorized.");
        return;
    }
    const newList = [...(selectedDesk.authorizedRequesters || []).map(u => u.id), user.id];
    const success = await updateDeskOnServer(selectedDesk.id, { authorizedRequesters: newList });
    if (success) {
        setIsAddAuthorizedRequesterOpen(false);
        toast.success("User authorized.");
    }
  };

  const handleRemoveAuthorizedRequester = async (email: string) => {
    if (!selectedDesk) return;
    const newList = selectedDesk.authorizedRequesters?.filter(u => u.email !== email).map(u => u.id);
    const success = await updateDeskOnServer(selectedDesk.id, { authorizedRequesters: newList });
    if (success) {
        toast.success("Authorization removed.");
    }
  };

  const handleToggleRestriction = async (restricted: boolean) => {
    if (!selectedDesk) return;
    const success = await updateDeskOnServer(selectedDesk.id, { submissionControl: restricted ? 'restricted' : 'open' });
    if (success) {
        toast.success(`Submission control set to ${restricted ? 'Restricted' : 'Open'}.`);
    }
  };

  const handleRemoveForm = async (formId: string) => {
    try {
        const response = await api.delete(`/api/service-desk/forms/${formId}`);
        if (response.success) {
            toast.success("Form removed successfully.");
            await fetchDesks();
        }
    } catch (error) {
        toast.error("Failed to remove form.");
    }
  };

  const handleRetireConfirm = async () => {
    if (!selectedDesk) return;
    try {
        const response = await api.patch(`/api/service-desk/${selectedDesk.id}/retire`, {});
        if (response.success) {
            toast.success(`Service desk "${selectedDesk.name}" has been retired.`);
            await fetchDesks();
            // Notify sidebar to refresh
            window.dispatchEvent(new CustomEvent('service-desks-changed'));
            setIsRetireModalOpen(false);
        }
    } catch (error) {
        toast.error("Failed to retire service desk.");
    }
  };

  return (
    <div className="space-y-6 pb-10 md:pb-0">
      <div className="flex justify-between items-center sm:items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-sm text-muted-foreground">Manage service desks, teams, and request templates</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Mobile Action */}
          <Button 
            size="icon"
            className="sm:hidden"
            onClick={() => navigate('/service-desk/new')}
          >
              <Plus className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Mobile Service Desk Selector */}
      <div className="sm:hidden w-full">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between bg-gray-50 border-gray-200 h-12 rounded-xl">
              <div className="flex items-center gap-2 overflow-hidden">
                <div className="bg-primary/10 p-1.5 rounded-lg shrink-0">
                  <Building2 className="w-4 h-4 text-primary" />
                </div>
                <div className="flex flex-col items-start truncate">
                  <span className="font-bold text-gray-900 truncate">{selectedDesk?.name || "Select Service Desk"}</span>
                  {selectedDesk?.isRetired && <span className="text-[10px] text-gray-500 uppercase font-black tracking-widest">Retired</span>}
                </div>
              </div>
              <ChevronDown size={18} className="text-slate-400 shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[calc(100vw-3rem)] max-h-[400px] overflow-y-auto p-0">
            <DropdownMenuLabel className="sticky top-0 z-30 bg-white py-3 px-4 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b shadow-sm">
              Active Service Desks
            </DropdownMenuLabel>
            <div className="p-2 space-y-1">
              {activeDesks.map((desk) => (
                <DropdownMenuItem 
                  key={desk.id} 
                  onClick={() => navigate(`/service-desk/settings/${desk.id}`)}
                  className={cn("gap-3 py-3 rounded-lg", id === desk.id && "bg-blue-50 text-blue-700 font-bold")}
                >
                  <div className="flex flex-col">
                    <span>{desk.name}</span>
                    <span className="text-[10px] opacity-60 font-medium">{desk.members.length} members • {desk.forms.length} forms</span>
                  </div>
                  {id === desk.id && <Check className="w-4 h-4 ml-auto" />}
                </DropdownMenuItem>
              ))}
            </div>

            {retiredDesks.length > 0 && (
              <>
                <DropdownMenuLabel className="sticky top-0 z-30 bg-white py-3 px-4 text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2 border-b shadow-sm">
                  <Archive className="w-3 h-3" /> Retired Service Desks
                </DropdownMenuLabel>
                <div className="p-2 space-y-1">
                  {retiredDesks.map((desk) => (
                    <DropdownMenuItem 
                      key={desk.id} 
                      onClick={() => navigate(`/service-desk/settings/${desk.id}`)}
                      className={cn("gap-3 py-3 rounded-lg opacity-80", id === desk.id && "bg-gray-100 text-gray-900 font-bold")}
                    >
                      <div className="flex flex-col">
                        <span>{desk.name}</span>
                        <span className="text-[10px] opacity-60 font-medium italic">Retired</span>
                      </div>
                      {id === desk.id && <Check className="w-4 h-4 ml-auto" />}
                    </DropdownMenuItem>
                  ))}
                </div>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-col sm:flex-row gap-6 sm:h-[calc(100vh-200px)]">
        {/* Left Pane - Sidebar (Desktop Only) */}
        <div className="hidden sm:flex w-full sm:w-80 flex-col bg-white border rounded-xl overflow-hidden min-h-[300px] sm:min-h-0">
          <div className="p-4 flex-1 sm:overflow-y-auto custom-scrollbar">
            {/* Sidebar Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Service Desks</h3>
              <button 
                onClick={() => navigate('/service-desk/new')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Read-Only Mode Banner (Visible if selected desk is retired) */}
            {isRetired && (
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Archive className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-600">Read-Only Mode</span>
                </div>
                <p className="text-xs text-gray-500">This service desk is retired. All settings are read-only.</p>
              </div>
            )}
            
            {/* Active Desks List */}
            <div className="space-y-2">
              {activeDesks.map((desk) => (
                <button 
                  key={desk.id}
                  onClick={() => navigate(`/service-desk/settings/${desk.id}`)}
                  className={cn(
                    "w-full text-left p-3 rounded-lg transition-colors",
                    id === desk.id ? "bg-blue-50/50" : "hover:bg-gray-50/50"
                  )}
                >
                  <div className="font-medium mb-1">{desk.name}</div>
                  <div className="text-sm text-gray-500">{desk.members.length} members • {desk.forms.length} request forms</div>
                </button>
              ))}
            </div>

            {/* Retired Desks Section */}
            {retiredDesks.length > 0 && (
              <div className="mt-6">
                <button 
                  onClick={() => setIsRetiredExpanded(!isRetiredExpanded)}
                  className="flex items-center justify-between w-full p-3 hover:bg-gray-50/50 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Archive className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-500">Retired Service Desks ({retiredDesks.length})</span>
                  </div>
                  <ChevronDown className={cn("w-4 h-4 text-gray-400 transition-transform", !isRetiredExpanded && "rotate-180")} />
                </button>

                {isRetiredExpanded && (
                  <div className="mt-2 space-y-2 pl-2">
                    {retiredDesks.map((desk) => (
                      <button 
                        key={desk.id}
                        onClick={() => navigate(`/service-desk/settings/${desk.id}`)}
                        className={cn(
                          "w-full text-left p-3 rounded-lg transition-colors border",
                          id === desk.id 
                            ? "border-gray-300 bg-gray-50/80" 
                            : "border-dashed bg-gray-50/30 border-gray-200 hover:bg-gray-50/50"
                        )}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-500">{desk.name}</span>
                          <span className="text-[10px] px-2 py-0.5 bg-gray-100 rounded text-gray-500 font-bold uppercase">Retired</span>
                        </div>
                        <div className="text-sm text-gray-400">{desk.members.length} members • {desk.forms.length} request forms</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Pane - Config Content */}
        <div className="flex-1 sm:overflow-y-auto bg-white border rounded-xl p-6 lg:p-8 custom-scrollbar">
          {selectedDesk && (
            <ServiceDeskDetails 
              config={selectedDesk}
              onUpdateAdmin={() => !isRetired && setIsChangeAdminOpen(true)}
              onAddMember={() => !isRetired && setIsAddModalOpen(true)}
              onRemoveMember={handleRemoveMember}
              onToggleRestriction={handleToggleRestriction}
              onAddAuthorizedRequester={() => !isRetired && setIsAddAuthorizedRequesterOpen(true)}
              onRemoveAuthorizedRequester={handleRemoveAuthorizedRequester}
              onAddForm={() => !isRetired && navigate(`/service-desk/${selectedDesk.id}/forms/new`)}
              onEditForm={(id) => !isRetired && navigate(`/service-desk/${selectedDesk.id}/forms/${id}/edit`)}
              onRemoveForm={handleRemoveForm}
              onRetire={() => !isRetired && setIsRetireModalOpen(true)}
            />
          )}
        </div>
      </div>

      <RetireServiceDeskModal 
        isOpen={isRetireModalOpen}
        onClose={() => setIsRetireModalOpen(false)}
        onConfirm={handleRetireConfirm}
        deskName={selectedDesk?.name || ""}
      />

      {/* Admin Change Modal */}
      {isChangeAdminOpen && (
        <MemberSelectModal 
          isOpen={isChangeAdminOpen}
          onClose={() => setIsChangeAdminOpen(false)}
          onAdd={handleChangeAdmin}
          deskTitle={selectedDesk?.name || ""}
          title="Change Team Administrator"
          stepDescription={`Select a user with management authority to lead ${selectedDesk?.name}`}
          selectedIds={selectedDesk?.teamAdmin?.id ? [selectedDesk.teamAdmin.id] : []}
          filterUser={(u) => u.permissions?.includes('manage:service_desk_team_admin') ?? false}
        />
      )}

      {/* Add Member Modal (Step 1) */}
      {isAddModalOpen && (
        <MemberSelectModal 
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleUserSelect}
          deskTitle={selectedDesk?.name || ""}
          selectedIds={selectedDesk?.members.map(m => String(m.id))}
          excludeIds={selectedDesk?.teamAdmin?.id ? [String(selectedDesk.teamAdmin.id)] : []}
          title="Add Team Member"
          stepDescription="Step 1: Select a user to join the support team"
        />
      )}

      {/* Authorized Requester Modal */}
      {isAddAuthorizedRequesterOpen && (
        <MemberSelectModal 
          isOpen={isAddAuthorizedRequesterOpen}
          onClose={() => setIsAddAuthorizedRequesterOpen(false)}
          onAdd={handleAddAuthorizedRequester}
          deskTitle={`Authorized Requester for ${selectedDesk?.name}`}
          selectedIds={selectedDesk?.authorizedRequesters?.map(u => u.id)}
          title="Authorize Requester"
          stepDescription="Select a user who can submit requests to this desk"
        />
      )}

      {/* Role Selection Modal (Step 2) */}
      {isRoleModalOpen && (
        <Dialog open={isRoleModalOpen} onOpenChange={setIsRoleModalOpen}>
          <DialogContent className="p-0 sm:max-w-md border-none overflow-hidden bg-white shadow-xl">
            <DialogHeader className="p-6 border-b border-border flex-row items-center gap-3 space-y-0 text-left">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <DialogTitle className="text-xl font-bold">Assign Role</DialogTitle>
            </DialogHeader>

            <div className="p-6">
              <DialogDescription className="text-sm text-muted-foreground mb-4">
                Step 2: Assign a role for {selectedUser?.name} in {selectedDesk?.name}
              </DialogDescription>

              <div className="space-y-3">
                <button 
                  onClick={() => setSelectedRole('Viewer')}
                  className={cn(
                    "w-full flex items-center gap-4 p-4 border rounded-xl transition-all text-left",
                    selectedRole === 'Viewer' 
                      ? "bg-primary/5 border-primary shadow-sm ring-1 ring-primary" 
                      : "bg-card border-border hover:border-primary/50"
                  )}
                >
                  <div className={cn(
                    "p-2 rounded-lg",
                    selectedRole === 'Viewer' ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  )}>
                    <Eye className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold">Viewer</div>
                    <div className="text-sm text-muted-foreground">Can view requests and comments only.</div>
                  </div>
                  {selectedRole === 'Viewer' && <Check className="w-5 h-5 text-primary" />}
                </button>

                {(() => {
                  const hasAgentPerm = selectedUser?.permissions?.includes('manage:service_desk_agent');
                  const hasViewerOnly = !hasAgentPerm && selectedUser?.permissions?.includes('manage:service_desk_agent_viewer');
                  const hasTeamAdminPerm = selectedUser?.permissions?.includes('manage:service_desk_team_admin');
                  const canBeTeamAdmin = hasTeamAdminPerm && !hasViewerOnly;
                  
                  return (
                    <>
                      <button 
                        onClick={() => !hasViewerOnly && setSelectedRole('Editor')}
                        disabled={hasViewerOnly}
                        className={cn(
                          "w-full flex items-center gap-4 p-4 border rounded-xl transition-all text-left",
                          selectedRole === 'Editor' 
                            ? "bg-blue-50 border-blue-200 shadow-sm ring-1 ring-blue-500" 
                            : "bg-card border-border hover:border-blue-500/50",
                          hasViewerOnly && "opacity-50 cursor-not-allowed grayscale-[0.5]"
                        )}
                      >
                        <div className={cn(
                          "p-2 rounded-lg",
                          selectedRole === 'Editor' ? "bg-blue-100 text-blue-600" : "bg-muted text-muted-foreground"
                        )}>
                          <SquarePen className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-bold">Editor</div>
                          <div className="text-sm text-muted-foreground">Can manage requests, add comments, and change status.</div>
                        </div>
                        {selectedRole === 'Editor' && <Check className="w-5 h-5 text-blue-600" />}
                      </button>

                      <button 
                        onClick={() => canBeTeamAdmin && setSelectedRole('Team Admin')}
                        disabled={!canBeTeamAdmin}
                        className={cn(
                          "w-full flex items-center gap-4 p-4 border rounded-xl transition-all text-left",
                          selectedRole === 'Team Admin' 
                            ? "bg-purple-50 border-purple-200 shadow-sm ring-1 ring-purple-500" 
                            : "bg-card border-border hover:border-purple-500/50",
                          !canBeTeamAdmin && "opacity-50 cursor-not-allowed grayscale-[0.5]"
                        )}
                      >
                        <div className={cn(
                          "p-2 rounded-lg",
                          selectedRole === 'Team Admin' ? "bg-purple-100 text-purple-600" : "bg-muted text-muted-foreground"
                        )}>
                          <Shield className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-bold">Team Admin</div>
                          <div className="text-sm text-muted-foreground">Full access to manage requests, forms, and team members.</div>
                        </div>
                        {selectedRole === 'Team Admin' && <Check className="w-5 h-5 text-purple-600" />}
                      </button>

                      {hasViewerOnly && (
                        <div className="px-2 flex items-center gap-2 text-[10px] text-amber-600 font-bold uppercase tracking-wider animate-in fade-in duration-300">
                          <Shield className="w-3 h-3" />
                          <span>Restricted to Viewer role based on system permissions</span>
                        </div>
                      )}

                      {!canBeTeamAdmin && !hasViewerOnly && (
                        <div className="px-2 flex items-center gap-2 text-[10px] text-gray-400 font-bold uppercase tracking-wider animate-in fade-in duration-300">
                          <Shield className="w-3 h-3" />
                          <span>Management authority or delegated permissions required</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            <DialogFooter className="p-6 border-t border-border flex items-center justify-end gap-3 bg-muted/30">
              <Button variant="ghost" onClick={() => { setIsRoleModalOpen(false); setIsAddModalOpen(true); }}>
                Back
              </Button>
              <Button onClick={handleConfirmAdd} className="px-6">
                Confirm & Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
